import { createContext, useContext, ReactNode, useEffect } from "react";
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import useAccountManagementStore from "../stores/accountManagementStore";
import { storeUserAccount } from "../utility/cache/storeUserAccount";
import { userDataType } from "../stores/accountManagementStore";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

console.log(supabaseUrl, supabaseAnonKey);


if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error("Missing Supabase environment variables");
}

export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
    },
});

interface SupabaseContextType {
    supabase: SupabaseClient;
}

const SupabaseContext = createContext<SupabaseContextType | undefined>(undefined);

export function SupabaseProvider({ children }: { children: ReactNode }) {
    const setUserData = useAccountManagementStore((state) => state.setUserData);

    useEffect(() => {
        // Clear corrupted session on mount if refresh fails
        const initAuth = async () => {
            try {
                const { data: { session }, error } = await supabase.auth.getSession();
                
                if (error) {
                    console.error("Session retrieval error:", error);
                    // Clear corrupted session
                    await supabase.auth.signOut();
                    setUserData(null);
                    storeUserAccount(null);
                } else if (session?.user) {
                    const userData: userDataType = {
                        id: session.user.id,
                        email: session.user.email || '',
                        providers: session.user.app_metadata?.providers || []
                    };
                    setUserData(userData);
                    storeUserAccount(userData);
                }
            } catch (err) {
                console.error("Init auth error:", err);
                // Clear everything on unexpected errors
                await supabase.auth.signOut();
                setUserData(null);
                storeUserAccount(null);
            }
        };

        initAuth();

        const { data: authListener } = supabase.auth.onAuthStateChange(
            async (event, session) => {
                console.log("Auth state:", event, session?.user?.id, session ? "session present" : "no session");
                
                if (event === 'TOKEN_REFRESHED') {
                    // console.log("Token refreshed successfully");
                    if (session?.user) {
                        const userData: userDataType = {
                            id: session.user.id,
                            email: session.user.email || '',
                            providers: session.user.app_metadata?.providers || []
                        };
                        setUserData(userData);
                        storeUserAccount(userData);
                    }
                    return;
                }

                if (event === 'SIGNED_OUT') {
                    // console.log("SIGNED_OUT event - clearing user data. Reason:", session ? "explicit logout" : "token expiration/refresh failed");
                    setUserData(null);
                    storeUserAccount(null);
                    return;
                }

                if (event === 'SIGNED_IN' && session?.user) {
                    const userData: userDataType = {
                        id: session.user.id,
                        email: session.user.email || '',
                        providers: session.user.app_metadata?.providers || []
                    };
                    setUserData(userData);
                    storeUserAccount(userData);
                }
            }
        );

        return () => {
            authListener.subscription.unsubscribe();
        };
    }, [setUserData]);


    

    return (
        <SupabaseContext.Provider value={{ supabase }}>
            {children}
        </SupabaseContext.Provider>
    );
}

export function useSupabase() {
    const context = useContext(SupabaseContext);
    if (context === undefined) {
        throw new Error("useSupabase must be used within a SupabaseProvider");
    }
    return context.supabase;
}